import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import SP_API from 'components/API';
import config from 'config.json';
import { spFastagUrl } from 'components/trackboardApi';
import useEmbedMode from 'hooks/useEmbedMode';
import { isEmbedded } from 'embed';

// Vision UI Dashboard PRO React example components
import Header from 'examples/Header';
import Footer from 'examples/Footer';
import driverImage from 'assets/images/tracking/truckFastag.png';

import vahanImage from 'assets/images/tracking/vahanHighresolution.png';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { LockOpen, Lock } from '@mui/icons-material';
import fastagLogo from 'assets/images/tracking/fastaglogohighresolution.png';
import sarathiImage from 'assets/images/tracking/sarathihighresolution.png';

import FastagDetails from './fastagDetails';
import styles from './styles.module.scss';

// Vision UI Dashboard PRO React context and components
import { useVisionUIController, setOpenConfigurator } from 'context';
import Configurator from 'components/Configurator';
import VuiBox from 'components/VuiBox';
import TickIcon from 'examples/Icons/TickIcon';

const perks = [
  'Instant Toll Transaction Verification',
  'Toll Movement Tracking',
  'Fraud Prevention & Security',
  'Comprehensive Vehicle Insights',
  'Get updates on Last updated location',
];

function TrackFastag() {
  const history = useHistory();
  const isEmbed = useEmbedMode();
  const isSsoEmbed = isEmbedded();
  const [controller, dispatch] = useVisionUIController();
  const { openConfigurator } = controller;
  const [vehicleNumber, setVehicleNumber] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [vehicleData, setVehicleData] = useState(null);
  const [showVehicleDetails, setShowVehicleDetails] = useState(false);
  const [loggedIn, setLoggedIn] = useState(false);
  const [userDetails, setUserDetails] = useState({});

  const validateVehicleNumber = (number) => {
    const checkForAlphaNumeric = (vNo) => {
      let letter = /[a-zA-Z]/;
      let number = /\d/;
      let valid = number.test(vNo) && letter.test(vNo); //match a letter _and_ a number
      return valid;
    };
    return checkForAlphaNumeric(number);
  };

  const handleConfiguratorOpen = () =>
    setOpenConfigurator(dispatch, !openConfigurator);

  const checkAuthentication = () => {
    const token = localStorage.getItem('token');
    return token !== null && token !== '';
  };

  // Check authentication status on component mount
  React.useEffect(() => {
    if (isSsoEmbed) {
      // Authenticated via the main app's SSO session - no standalone login.
      setLoggedIn(true);
      loadInput();
      return;
    }
    const isAuthenticated = checkAuthentication();
    setLoggedIn(isAuthenticated);
    loadInput();

    if (isAuthenticated) {
      // Get user details from localStorage
      const storedUserDetails = localStorage.getItem('userDetails');
      if (storedUserDetails) {
        try {
          const userDetails = JSON.parse(storedUserDetails);
          setUserDetails(userDetails);
        } catch (e) {
          console.error('Error parsing user details:', e);
          setUserDetails({ name: 'User' });
        }
      } else {
        setUserDetails({ name: 'User' });
      }
    }
  }, []);

  const loadInput = () => {
    const match = document.cookie.match(/(^| )tempInput=([^;]+)/);
    if (match) {
      setVehicleNumber(decodeURIComponent(match[2]));
      document.cookie =
        'tempInput=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    }
  };

  const saveInput = () => {
    const expires = new Date(Date.now() + 30 * 1000).toUTCString(); // 30 sec
    document.cookie = `tempInput=${encodeURIComponent(
      vehicleNumber,
    )}; expires=${expires}; path=/`;
  };

  const handleVerify = async () => {
    if (!isSsoEmbed && !checkAuthentication()) {
      // Standalone mode still requires the IFI login. In embed mode the host
      // app's SSO token (attached by SP_API) is all that's needed.
      saveInput();
      handleConfiguratorOpen();
      return;
    }

    // Clear previous errors and data
    setError('');
    setShowVehicleDetails(false);
    setVehicleData(null);

    // Validate vehicle number
    if (!vehicleNumber.trim()) {
      setError('Please enter a vehicle registration number');
      return;
    }

    if (!validateVehicleNumber(vehicleNumber.trim())) {
      setError('Please enter a valid vehicle registration number');
      return;
    }

    setIsLoading(true);

    try {
      let response;
      if (isSsoEmbed) {
        // Embed mode: call the SuperProcure backend trackboard endpoint
        // directly (not IFI's own API). freighterId / tripId are not sent;
        // refresh:true forces the ONETIMEFASTAG- workflow to re-run every time.
        response = await SP_API({
          method: 'POST',
          url: spFastagUrl(),
          data: {
            licensePlate: vehicleNumber.trim(),
            refresh: true,
          },
        });
      } else {
        response = await SP_API({
          method: 'POST',
          url: config.FASTAG_API,
          data: {
            licensePlate: vehicleNumber.trim(),
          },
        });
      }

      if (response && response.data) {
        setVehicleData(response.data);
        setShowVehicleDetails(true);
      } else {
        setError('Vehicle not found or invalid registration number');
      }
    } catch (error) {
      if (!isSsoEmbed) {
        // Skipped in embed mode - the error carries the request headers.
        console.error('API Error:', error);
      }
      if (isSsoEmbed && error?.status === 401) {
        setError(
          'Your session could not be verified. Please reload this view from SuperProcure.',
        );
        return;
      }
      if (error?.status === 400) {
        setError(
          error?.data || 'Failed to fetch vehicle details. Please try again.',
        );
        return;
      }
      setError('Failed to fetch vehicle tracking details. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseVehicleDetails = () => {
    setShowVehicleDetails(false);
    setVehicleData(null);
    setVehicleNumber('');
  };

  const handleVehicleInfoClick = () => {
    saveInput();
    history.push('/verify-vahan');
  };

  const handleVerifyDriver = () => {
    history.push('/verify-sarathi');
  };

  let lastLocation = '';
  let status = 'Inactive';

  if (vehicleData && vehicleData.length) {
    lastLocation = vehicleData[0].address;
    status = 'Active';
  }

  // Floating lock button (same as homepage)
  const configsButton = (
    <VuiBox
      id="floating_lock_button"
      display="flex"
      justifyContent="center"
      alignItems="center"
      width="3.5rem"
      height="3.5rem"
      bgColor="white"
      shadow="sm"
      borderRadius="50%"
      position="fixed"
      right="2rem"
      bottom="5rem"
      zIndex={99}
      color="white"
      sx={({ palette: { info, success } }) => ({
        cursor: 'pointer',
        backgroundColor: loggedIn ? success.main : info.main,
      })}
      onClick={handleConfiguratorOpen}
    >
      {loggedIn ? (
        <Lock id="floating_lock_button" fontSize="default" color="inherit">
          {userDetails.name ? userDetails.name.split(' ')[0] : 'User'}
        </Lock>
      ) : (
        <LockOpen id="floating_lock_button" fontSize="default" color="inherit">
          Login
        </LockOpen>
      )}
    </VuiBox>
  );

  return (
    <>
      {!isEmbed && <Header />}

      <main className={styles.heroSection}>
        {!showVehicleDetails && (
          <div className={styles.heroContent}>
            <div className={styles.ftTruckIllustration}>
              <img
                src={driverImage}
                alt="Vehicle"
                className={styles.truckImage}
              />
            </div>
            <div className={styles.vahanTextContainer}>
              <div className={styles.ftLogoContainer}>
                <h1 className={styles.ftMainHeadline}>Track via Fastag!</h1>
                <img
                  src={fastagLogo}
                  alt="Fastag Logo"
                  className={styles.ftLogoImage}
                />
              </div>

              <p className={styles.ftSubtitle}>
                Get real-time vehicle movement updates via toll data to reduce
                delays and keep your shipments on track.
              </p>
            </div>
          </div>
        )}

        <div className={styles.inputSectionContainer}>
          {!showVehicleDetails ? (
            // Initial input section
            <form className={styles.inputElementsContainer}>
              <input
                type="text"
                placeholder="Enter Vehicle Registration No., eg KA03N2536"
                value={vehicleNumber}
                onChange={(e) => setVehicleNumber(e.target.value)}
                className={styles.vehicleInput}
              />
              <button
                onClick={handleVerify}
                disabled={isLoading}
                className={styles.verifyButton}
              >
                {isLoading ? (
                  'Verifying...'
                ) : (
                  <>
                    <span>Get Details</span>
                  </>
                )}
              </button>
            </form>
          ) : (
            // Verified input section (rendered after Get Details verification)
            <form className={styles.inputElementsContainerVerified}>
              {' '}
              <div
                className={styles.backButtonResponsive}
                onClick={handleCloseVehicleDetails}
              >
                <ChevronLeftIcon fontSize="medium" />
                <span className={styles.backText}>Back</span>
              </div>
              <input
                type="text"
                placeholder="Enter Vehicle Registration No., eg KA03N2536"
                value={vehicleNumber}
                onChange={(e) => setVehicleNumber(e.target.value)}
                className={styles.vehicleInputVerified}
              />
              <button
                onClick={handleVerify}
                disabled={isLoading}
                className={styles.verifyButtonVerified}
              >
                {isLoading ? (
                  'Verifying...'
                ) : (
                  <>
                    <span>Get</span>{' '}
                    <span className={styles.detailsText}>Details</span>
                  </>
                )}
              </button>
              {/* Only the verified section has this button */}
              <div
                className={styles.verifyDriverButtonVerified}
                onClick={handleVerifyDriver}
              >
                <img
                  src={sarathiImage}
                  alt="Driver"
                  className={styles.sarathiImageVerified}
                />
                <span className={styles.sarathiTextVerified}>
                  Verify Driver
                </span>
              </div>
            </form>
          )}
        </div>

        {error && <div className={styles.errorMessage}>{error}</div>}

        {showVehicleDetails && (
          <div className={styles.vehicleDetailsCard}>
            <div className={styles.vehicleHeader}>
              <div className={styles.vehicleDataContainer}>
                <div className={styles.vehicleNumberSection}>
                  <h2 className={styles.vehicleNumber}>{vehicleNumber}</h2>
                  <div className={styles.vahanBrand}>
                    <img
                      src={fastagLogo}
                      alt="Fastag Logo"
                      className={styles.fastagLogoImage}
                    />
                  </div>
                </div>

                <div
                  className={styles.fastagButton}
                  onClick={handleVehicleInfoClick}
                >
                  <img
                    src={vahanImage}
                    alt="Fastag Logo"
                    className={styles.fastagLogo}
                  />
                  <span className={styles.buttonText}>Check Vehicle Info</span>
                </div>
              </div>

              <div className={styles.vehicleStatusPanels}>
                <div
                  className={`${styles.statusPanel} ${styles.firstStatusPanel}`}
                >
                  <span className={styles.statusLabel}>Last Location</span>
                  <span className={styles.statusValue}>{lastLocation}</span>
                </div>
                <div className={styles.statusPanel}>
                  <span className={styles.statusLabel}>Status</span>
                  <span className={`${styles.statusValue} ${styles.rcActive}`}>
                    {status}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      <section className={styles.bottomSection}>
        {!showVehicleDetails && (
          <div className={styles.perks}>
            <div className={styles.bottomHeading}>
              Fastag-based vehicle tracking for last toll location update
            </div>
            <div className={styles.perksList}>
              {perks.map((perk, index) => (
                <div key={index} className={styles.perksListItem}>
                  <TickIcon
                    className={styles.tickIcon}
                    width={20}
                    height={20}
                    fill="#A2B2C4"
                  />
                  <p className={styles.perksListItemText}>{perk}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {showVehicleDetails && vehicleData && (
          <FastagDetails vehicleData={vehicleData} />
        )}
      </section>

      {!isEmbed && <Footer />}
      {!isSsoEmbed && (
        <>
          <Configurator
            userDetails={loggedIn ? userDetails : null}
            isShowConfig={!openConfigurator}
          />
          {configsButton}
        </>
      )}
    </>
  );
}

export default TrackFastag;
